import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentMRS, setRecentMRS] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [analyticsRes, mrsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard?user_id=${user.id}`),
        axios.get(`${API}/mrs?user_id=${user.id}`)
      ]);
      
      setAnalytics(analyticsRes.data);
      setRecentMRS(mrsRes.data.slice(0, 5)); // Show only recent 5
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      'Cardiology': 'bg-red-100 text-red-800',
      'Endocrinology': 'bg-green-100 text-green-800',
      'Neurology': 'bg-sky-100 text-sky-800',
      'Oncology': 'bg-purple-100 text-purple-800',
      'Psychiatry': 'bg-indigo-100 text-indigo-800',
      'Orthopedics': 'bg-orange-100 text-orange-800',
      'Pediatrics': 'bg-pink-100 text-pink-800',
      'Geriatrics': 'bg-yellow-100 text-yellow-800'
    };
    return colors[specialty] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="rounded-lg shadow-lg text-white p-6" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-hover) 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Welcome back, Dr. {user?.name?.split(' ')[0]}</h1>
            <p className="text-white/80 mt-2">
              {user?.specialty && `${user.specialty} Specialist • `}
              {user?.institution || 'Medical Professional'}
            </p>
          </div>
          <div className="hidden md:block">
            <div className="h-16 w-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="clinical-card clinical-card-content">
          <div className="flex items-center">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'var(--theme-primary-light)', color: 'var(--theme-primary)' }}>
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <div className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--theme-text-primary)' }}>{analytics?.mrs_count || 0}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>Medical Record Sets</div>
            </div>
          </div>
        </div>

        <div className="clinical-card clinical-card-content">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <div className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--theme-text-primary)' }}>{analytics?.total_patients || 0}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>Total Patients</div>
            </div>
          </div>
        </div>

        <div className="clinical-card clinical-card-content">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-4">
              <div className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--theme-text-primary)' }}>{analytics?.recent_conversations || 0}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>AI Conversations</div>
            </div>
          </div>
        </div>

        <div className="clinical-card clinical-card-content">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 text-orange-600">
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
              </svg>
            </div>
            <div className="ml-4">
              <div className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--theme-text-primary)' }}>{Object.keys(analytics?.specialty_distribution || {}).length}</div>
              <div className="text-sm font-medium" style={{ color: 'var(--theme-text-secondary)' }}>Specialties</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <Link to="/mrs" className="group clinical-card hover:shadow-lg transition-all duration-200">
          <div className="clinical-card-content">
            <div className="flex items-center space-x-4">
              <div className="p-3 rounded-lg transition-colors duration-200" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                <svg className="h-6 w-6" style={{ color: 'var(--theme-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Create New MRS</h3>
                <p className="text-gray-600 text-sm">Start a new medical record set</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/ai-chat" className="group medical-card hover:shadow-lg transition-all duration-200">
          <div className="medical-card-content">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors duration-200">
                <svg className="h-6 w-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">AI Assistant</h3>
                <p className="text-gray-600 text-sm">Analyze data with AI</p>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/templates" className="group medical-card hover:shadow-lg transition-all duration-200">
          <div className="medical-card-content">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors duration-200">
                <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Form Templates</h3>
                <p className="text-gray-600 text-sm">Use specialty forms</p>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Recent Medical Record Sets */}
      <div className="medical-card">
        <div className="medical-card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Medical Record Sets</h2>
            <Link to="/mrs" className="text-sky-600 hover:text-sky-800 text-sm font-medium">
              View all →
            </Link>
          </div>
        </div>
        <div className="medical-card-content">
          {recentMRS.length > 0 ? (
            <div className="space-y-4">
              {recentMRS.map((mrs) => (
                <div key={mrs.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="h-10 w-10 bg-sky-100 rounded-lg flex items-center justify-center">
                      <svg className="h-5 w-5 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{mrs.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`specialty-badge ${getSpecialtyColor(mrs.specialty)}`}>
                          {mrs.specialty}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {mrs.patient_count} patients
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/mrs/${mrs.id}/upload`}
                      className="btn-secondary text-xs px-3 py-1"
                    >
                      Upload Data
                    </Link>
                    <Link
                      to={`/mrs/${mrs.id}/patients`}
                      className="btn-primary text-xs px-3 py-1"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Medical Record Sets</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first medical record set.</p>
              <Link to="/mrs" className="btn-primary">
                Create New MRS
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Research & Analytics Section */}
      <div className="medical-card">
        <div className="medical-card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Research & Analytics</h2>
            <Link to="/analytics" className="text-sky-600 hover:text-sky-800 text-sm font-medium">
              View Details →
            </Link>
          </div>
        </div>
        <div className="medical-card-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="p-4 bg-sky-50 hover:bg-sky-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-sky-100 rounded-lg">
                  <svg className="h-5 w-5 text-sky-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Cohort Analysis</h4>
                  <p className="text-xs text-gray-600">Compare patient groups</p>
                </div>
              </div>
            </button>

            <button className="p-4 bg-green-50 hover:bg-green-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Trend Analysis</h4>
                  <p className="text-xs text-gray-600">Identify patterns</p>
                </div>
              </div>
            </button>

            <button className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="h-5 w-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Efficacy Review</h4>
                  <p className="text-xs text-gray-600">Treatment success rates</p>
                </div>
              </div>
            </button>

            <Link to="/ai-chat" className="p-4 bg-indigo-50 hover:bg-indigo-100 rounded-lg text-left transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <svg className="h-5 w-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">AI Analysis</h4>
                  <p className="text-xs text-gray-600">Intelligent insights</p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Onboarding Tips */}
      <div className="medical-card bg-gradient-to-r from-green-50 to-sky-50 border-green-200">
        <div className="medical-card-content">
          <div className="flex items-start space-x-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Getting Started with Exlva DARe</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center space-x-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full text-xs flex items-center justify-center font-semibold">1</span>
                  <span>Create a Medical Record Set for your specialty or study</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full text-xs flex items-center justify-center font-semibold">2</span>
                  <span>Upload Excel/CSV files or use our form templates for data entry</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full text-xs flex items-center justify-center font-semibold">3</span>
                  <span>Use AI Assistant for clinical insights and research analysis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;